import { createAction } from '@reduxjs/toolkit';
import type { RecordOf } from 'immutable';
import type { Map as ImMap } from 'immutable';
import type { Account } from '../accounts/accountsTypes';
import { type Meta, getPayloadCreator } from '../core/actionHelpers';
import type { QcsSyncResourcesPayload } from '../core/resourceStoreTypes';
import type { FiIssueType } from '../institutions/institutionsTypes';
import type {
  InstitutionLogin,
  InstitutionLoginClient,
  InstitutionLoginProps,
  InstitutionLoginQcsAction,
  InstitutionLoginStatus,
  MfaResponse,
  RefreshResponse,
} from './institutionLoginsTypes';

const institutionLoginPayloadCreator = getPayloadCreator<InstitutionLogin>();
const institutionLoginWithIdPayloadCreator = getPayloadCreator<{
  institutionLogin: InstitutionLogin & { id: string };
}>();
const syncInstitutionLoginPayloadCreator = getPayloadCreator<QcsSyncResourcesPayload<InstitutionLoginProps>>();

const errorPayloadCreator = getPayloadCreator<{ id?: string; message?: string }>();

const metaWithPromisePayloadCreator = <P, R = () => void>(
  payload: P,
  meta: Meta & {
    resolve: R;
    reject: (error: any) => void;
  },
) => ({ payload, meta });

export const getInstitutionLogins = createAction('GET_INSTITUTION_LOGINS', getPayloadCreator());
export const getInstitutionLoginsSuccess = createAction(
  'GET_INSTITUTION_LOGINS_SUCCESS',
  syncInstitutionLoginPayloadCreator,
);
export const getInstitutionLoginsFailure = createAction('GET_INSTITUTION_LOGINS_FAILURE', errorPayloadCreator);

export const getLoginsWithFiIssues = createAction('GET_LOGINS_WITH_FI_ISSUES', getPayloadCreator());
export const getLoginsWithFiIssuesSuccess = createAction(
  'GET_LOGINS_WITH_FI_ISSUES_SUCCESS',
  getPayloadCreator<ImMap<string, InstitutionLogin>>(),
);

export const updateInstitutionLogin = createAction('UPDATE_INSTITUTION_LOGIN', institutionLoginPayloadCreator);
export const updateInstitutionLoginSuccess = createAction(
  'UPDATE_INSTITUTION_LOGIN_SUCCESS',
  institutionLoginPayloadCreator,
);
export const updateInstitutionLoginFailure = createAction('UPDATE_INSTITUTION_LOGIN_FAILURE', errorPayloadCreator);

export const deleteInstitutionLogin = createAction('DELETE_INSTITUTION_LOGIN', institutionLoginPayloadCreator);
export const deleteInstitutionLoginSuccess = createAction(
  'DELETE_INSTITUTION_LOGIN_SUCCESS',
  institutionLoginPayloadCreator,
);
export const deleteInstitutionLoginFailure = createAction('DELETE_INSTITUTION_LOGIN_FAILURE', errorPayloadCreator);

export const addInstitutionLoginAndAccounts = createAction(
  'ADD_INSTITUTION_LOGIN_AND_ACCOUNTS',
  getPayloadCreator<{ institutionLogin: InstitutionLoginClient; accounts: Account[] }>(),
);
export const addInstitutionLoginAndAccountsResponse = createAction(
  'ADD_INSTITUTION_LOGIN_AND_ACCOUNTS_RESPONSE',
  institutionLoginWithIdPayloadCreator,
);

export const refreshAccountsTriggeredCredentialsRequest = createAction(
  'REFRESH_ACCOUNTS_TRIGGERED_CREDENTIALS_REQUEST',
  getPayloadCreator<string>(),
);

export const refreshAccountsCredentialsProvided = createAction(
  'REFRESH_ACCOUNTS_CREDENTIALS_PROVIDED',
  getPayloadCreator<{ institutionLogin: InstitutionLoginProps }>(),
);
export const refreshAccountsCredentialsNotProvided = createAction(
  'REFRESH_ACCOUNTS_CREDENTIALS_NOT_PROVIDED',
  getPayloadCreator<string>(),
);

export const institutionLoginSetCredentialsBlob = createAction(
  'INSTITUTION_LOGIN_SET_CREDENTIALS_BLOB',
  getPayloadCreator<{ credentialsBlob: string; institutionLoginId: string }>(),
);

export const qcsInstitutionLoginAction = createAction(
  'QCS_INSTITUTION_LOGIN_ACTION',
  metaWithPromisePayloadCreator<{ id: string; params?: any; qcsAction: InstitutionLoginQcsAction }>,
);
export const qcsInstitutionLoginActionResponse = createAction(
  'QCS_INSTITUTION_LOGIN_ACTION_RESPONSE',
  institutionLoginPayloadCreator,
);

export const updateChangedInstitutionLogins = createAction(
  'UPDATE_IMPACTED_INSTITUTION_LOGINS',
  getPayloadCreator<RecordOf<InstitutionLoginProps>[]>(),
);

export const refreshAccounts = createAction(
  'REFRESH_ACCOUNTS',
  getPayloadCreator<{ institutionLoginId?: string; autoRefresh?: boolean } | void>(),
);
export const refreshAccountsPrecheck = createAction('REFRESH_ACCOUNTS_PRECHECK');
export const refreshAccountsStatuses = createAction('REFRESH_ACCOUNTS_STATUSES', getPayloadCreator<RefreshResponse>());
export const refreshAccountsCompleted = createAction(
  'REFRESH_ACCOUNTS_COMPLETED',
  getPayloadCreator<{ institutionLoginId?: string | null; status: InstitutionLoginStatus }>(),
);

export const refreshAccountsTriggeredMfa = createAction('REFRESH_ACCOUNTS_TRIGGERED_MFA', getPayloadCreator<string>());

export const refreshAccountsMfaFormSubmit = createAction(
  'REFRESH_ACCOUNTS_MFA_FORM_SUBMIT',
  metaWithPromisePayloadCreator<{ mfaResponse: MfaResponse }, (loginId: string) => void>,
);
export const refreshAccountsMfaFormSubmitResponse = createAction(
  'REFRESH_ACCOUNTS_MFA_FORM_SUBMIT_RESPONSE',
  getPayloadCreator<{ institutionLoginId: string; error?: Error }>(),
);
export const refreshAccountsMfaNotProvided = createAction(
  'REFRESH_ACCOUNTS_MFA_NOT_PROVIDED',
  getPayloadCreator<string>(),
);

export const addDismissedFiIssue = createAction(
  'ADD_DISMISSED_FI_ISSUE',
  getPayloadCreator<{ id: string; type: FiIssueType }>(),
);

export const credentialsFormSubmitSuccess = createAction(
  'CREDENTIALS_FORM_SUBMIT_SUCCESS',
  institutionLoginWithIdPayloadCreator,
);

export const batchInstitutionLogins = createAction('BATCH_INSTITUTION_LOGINS', syncInstitutionLoginPayloadCreator);
export const batchInstitutionLoginsSuccess = createAction(
  'BATCH_INSTITUTION_LOGINS_SUCCESS',
  syncInstitutionLoginPayloadCreator,
);
export const batchInstitutionLoginsFailure = createAction('BATCH_INSTITUTION_LOGINS_FAILURE', errorPayloadCreator);

// This is used for Apple Wallet. This updates refreshStatus/aggStatus of Institution Login.
export const updateRefreshStatus = createAction(
  'UPDATE_INSTITUTION_LOGIN_REFRESH_STATUS',
  metaWithPromisePayloadCreator<RecordOf<InstitutionLoginProps>, (loginId: string) => void>,
);
